import { useEffect, useState } from 'react'
import type { EnhanceOptions } from '../api/types'
import { useChooseAudio, useDiscardEnhanced, useEnhanceSound, usePreviewCuts, useSound } from '../hooks/useSound'
import { formatBytes, formatDuration, timeAgo } from '../utils/format'
import { Badge, Button, Card, ErrorNote, LoadingBlock, ProgressBar } from './ui'

const SAFE: EnhanceOptions = { loudness: true, denoise: true, trim_pauses: false, cut_fillers: false }

const STAGES: Record<string, string> = {
  download: 'Fetching the recording',
  clean: 'Cutting and cleaning',
  measure: 'Measuring the loudness',
  encode: 'Writing the new audio',
  upload: 'Saving it',
}

const CHOICES: { key: keyof EnhanceOptions; label: string; hint: string; needsTranscript?: boolean }[] = [
  { key: 'loudness', label: 'Even out the levels', hint: 'Brings the whole episode to the loudness podcast apps expect (-16 LUFS).' },
  { key: 'denoise', label: 'Take off noise', hint: 'Removes rumble, hum and steady hiss. It does not fix a bad room.' },
  { key: 'trim_pauses', label: 'Shorten long pauses', hint: 'Pauses over a second and a half become a short breath.', needsTranscript: true },
  { key: 'cut_fillers', label: 'Cut hesitations', hint: 'Drops "um", "uh" and the like.', needsTranscript: true },
]

/** Clean up an episode's audio, and choose which version listeners hear. */
export function SoundCard({ workspaceId, episodeId, canEdit }: { workspaceId: string; episodeId: string; canEdit: boolean }) {
  const sound = useSound(workspaceId, episodeId)
  const enhance = useEnhanceSound(workspaceId, episodeId)
  const choose = useChooseAudio(workspaceId, episodeId)
  const discard = useDiscardEnhanced(workspaceId, episodeId)
  const preview = usePreviewCuts(workspaceId, episodeId)
  const [options, setOptions] = useState<EnhanceOptions>(SAFE)
  const data = sound.data

  // Start from what the last run did, so running it again is one click.
  useEffect(() => {
    if (data?.options) setOptions(data.options)
  }, [data?.options])

  // Say how much would go, whenever the cutting choices change.
  const { mutate: askPreview } = preview
  const cutting = options.trim_pauses || options.cut_fillers
  useEffect(() => {
    if (cutting && data?.has_transcript) askPreview(options)
  }, [askPreview, cutting, data?.has_transcript, options])

  if (sound.isPending) return <LoadingBlock />
  if (sound.isError) return <ErrorNote error={sound.error} />
  if (!data?.available) return null

  const nothing = !options.loudness && !options.denoise && !options.trim_pauses && !options.cut_fillers
  const removing = cutting ? preview.data?.removing_ms : 0
  const error = enhance.error ?? choose.error ?? discard.error
  return (
    <Card
      title="Sound"
      actions={data.enhanced && (data.used_by_listeners ? <Badge tone="ok">Cleaned up</Badge> : <Badge>Original</Badge>)}
    >
      <div className="stack">
        {data.running ? (
          <div className="stack-sm">
            <ProgressBar value={data.progress ?? 0} label="Cleaning up the audio" />
            <span className="muted small">{STAGES[data.stage ?? ''] ?? 'Cleaning up the audio'}. It takes about as long as the episode.</span>
          </div>
        ) : (
          <>
            {data.enhanced && (
              <div className="sound-result">
                <div className="row between">
                  <strong>Cleaned up {timeAgo(data.enhanced_at)}</strong>
                  <span className="stat-chips">
                    {data.removed_ms ? <span className="stat-chip">{formatDuration(data.removed_ms)} cut</span> : null}
                    {data.loudness_in != null ? (
                      <span className="stat-chip">{data.loudness_in.toFixed(1)} → {data.loudness_out?.toFixed(1)} LUFS</span>
                    ) : null}
                    {data.bytes ? <span className="stat-chip">{formatBytes(data.bytes)}</span> : null}
                  </span>
                </div>
                <p className="muted small">
                  {data.used_by_listeners
                    ? 'Listeners hear the cleaned audio. The original is still here.'
                    : 'Listeners hear the original. The cleaned audio is kept for comparison.'}
                </p>
                {canEdit && (
                  <div className="row">
                    <Button variant="secondary" busy={choose.isPending} onClick={() => choose.mutate(!data.used_by_listeners)}>
                      {data.used_by_listeners ? 'Go back to the original' : 'Use the cleaned audio'}
                    </Button>
                    <Button
                      variant="ghost"
                      busy={discard.isPending}
                      onClick={() => {
                        if (window.confirm('Throw the cleaned audio away? Listeners go back to the original.')) discard.mutate()
                      }}
                    >
                      Throw it away
                    </Button>
                  </div>
                )}
              </div>
            )}
            {data.error && !data.enhanced && <ErrorNote message={`The last clean-up failed: ${data.error}`} />}
            {canEdit && (
              <form
                className="stack"
                onSubmit={(event) => {
                  event.preventDefault()
                  enhance.mutate(options)
                }}
              >
                {!data.enhanced && <p className="muted">Clean up the audio listeners hear. Pick what to fix:</p>}
                <div className="sound-options">
                  {CHOICES.map((choice) => {
                    const blocked = choice.needsTranscript && !data.has_transcript
                    return (
                      <label key={choice.key} className={`option-row${blocked ? ' is-blocked' : ''}`}>
                        <input
                          type="checkbox"
                          checked={options[choice.key] && !blocked}
                          disabled={blocked}
                          onChange={(e) => setOptions({ ...options, [choice.key]: e.target.checked })}
                        />
                        <span>
                          <strong>{choice.label}</strong>
                          <span className="muted small">{choice.hint}</span>
                          {blocked && <span className="pill-note">Needs the transcript</span>}
                        </span>
                      </label>
                    )
                  })}
                </div>
                {removing ? <p className="muted small">About {formatDuration(removing)} would come out.</p> : null}
                <div className="form-footer">
                  <Button type="submit" busy={enhance.isPending} disabled={nothing}>
                    {data.enhanced ? 'Clean up again' : 'Clean up the sound'}
                  </Button>
                  <span className="muted small">Free · the original recording is never changed</span>
                </div>
              </form>
            )}
          </>
        )}
        {error && <ErrorNote error={error} />}
      </div>
    </Card>
  )
}
